/**
 * Renders one procedural tree per side margin — rooted at the bottom,
 * thick tapered trunk (bare), a few thin forking branches that taper to
 * points, each tipped with one small simple leaf — as a single fixed,
 * translucent SVG overlay behind the studio content. On
 * first paint the tree fades in as one unit so it reads as growing up
 * from the base.
 *
 * Colour is a single warm tone-on-tone hue driven by the `--branch-wood`
 * / `--branch-leaf` CSS variables (defined per theme in studio.css), so
 * the field re-tints automatically on light ↔ dark.
 *
 * `getPerchPoints()` exposes the thick, near-horizontal stretch of each
 * branch (lifted just above the wood) so the bird has a comfortable
 * place to nest. On narrow viewports there's no margin to draw into, so
 * the field hides itself and reports zero perch points — the widget then
 * falls back to corner nesting.
 */

import { generateField, type FieldGeometry, type PerchPoint } from "./generate";
import { FOLIAGE_MARKUP } from "./foliageAsset";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Width of the clean right-hand strip reserved for the tree. The layout
 * keeps panels out of this band (see `.studio-workspace` padding-right in
 * studio.css) so the silhouette has free space. Keep these two values in
 * sync.
 */
const RIGHT_GUTTER_PX = 200;
/**
 * Below this viewport width there isn't room to reserve the gutter
 * without crowding the workspace, so the field hides and the bird falls
 * back to corner nesting. Mirrors the `min-width` media query in
 * studio.css that reserves the gutter.
 */
const MIN_VIEWPORT_PX = 1180;

/**
 * Intro: the trees "grow" up from the ground — a clip wipe revealing the
 * silhouette bottom-to-top — with a quick opacity fade so the leading
 * edge stays soft. Runs once on first paint; resizes snap (see `render`).
 */
const GROW_IN_MS = 1400;
const FADE_IN_MS = 700;
const RESIZE_DEBOUNCE_MS = 200;
/** Matches studio.css `--ease-quart` for a soft, settling motion. */
const GROW_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

export interface BranchFieldOptions {
  /** Seed for a reproducible layout. Random per load if omitted. */
  seed?: number;
}

export class BranchField {
  private readonly host: HTMLElement;
  private readonly baseSeed: number;
  private readonly svg: SVGSVGElement;
  private readonly prefersReducedMotion: boolean;

  private perchPoints: PerchPoint[] = [];
  private resizeTimerId: number | null = null;
  private lastWidth = 0;
  private lastHeight = 0;

  constructor(host: HTMLElement, options: BranchFieldOptions = {}) {
    this.host = host;
    this.baseSeed = (options.seed ?? Math.floor(Date.now() % 2147483647)) >>> 0;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.svg = document.createElementNS(SVG_NS, "svg");
    this.svg.classList.add("koko-branch-field");
    this.svg.setAttribute("aria-hidden", "true");
    Object.assign(this.svg.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: "0",
    } satisfies Partial<CSSStyleDeclaration>);
    this.host.appendChild(this.svg);

    window.addEventListener("resize", this.handleResize, { passive: true });

    // First render animates; resizes snap (see `render`).
    this.render(true);
  }

  /**
   * Perch targets in viewport coordinates, or an empty array when the
   * field is hidden (narrow viewport). The widget treats an empty list
   * as "no tree — use corners".
   */
  getPerchPoints(): PerchPoint[] {
    return this.perchPoints;
  }

  destroy(): void {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimerId !== null) window.clearTimeout(this.resizeTimerId);
    this.svg.remove();
  }

  private handleResize = (): void => {
    if (this.resizeTimerId !== null) window.clearTimeout(this.resizeTimerId);
    this.resizeTimerId = window.setTimeout(() => {
      this.resizeTimerId = null;
      if (window.innerWidth === this.lastWidth && window.innerHeight === this.lastHeight) {
        return;
      }
      this.render(false);
    }, RESIZE_DEBOUNCE_MS);
  };

  private render(animate: boolean): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.lastWidth = width;
    this.lastHeight = height;

    this.svg.replaceChildren();
    this.svg.setAttribute("width", String(width));
    this.svg.setAttribute("height", String(height));

    if (width < MIN_VIEWPORT_PX) {
      this.svg.style.display = "none";
      this.perchPoints = [];
      return;
    }
    this.svg.style.display = "";

    const field = generateField({
      width,
      height,
      seed: this.baseSeed,
      marginWidth: RIGHT_GUTTER_PX,
    });
    this.paint(field, animate && !this.prefersReducedMotion);
    this.perchPoints = field.perchPoints;
  }

  private paint(field: FieldGeometry, animate: boolean): void {
    // One wrapper group holds the whole tree. Fading it as a unit keeps
    // the silhouette flat throughout the intro — no per-part overlap
    // compositing. The flat tone-on-tone opacity lives on the SVG (CSS).
    const tree = document.createElementNS(SVG_NS, "g");
    const limbGroup = document.createElementNS(SVG_NS, "g");
    const leafGroup = document.createElementNS(SVG_NS, "g");

    for (const limb of field.limbs) {
      limbGroup.appendChild(this.makePath(limb.d, "koko-limb"));
    }
    const template = this.foliageTemplate();
    for (const sprig of field.foliage) {
      const g = document.createElementNS(SVG_NS, "g");
      g.setAttribute("class", "koko-foliage");
      g.setAttribute("transform", sprig.transform);
      g.appendChild(template.cloneNode(true));
      leafGroup.appendChild(g);
    }

    // Leaves above the wood so foliage reads in front of the branches.
    tree.appendChild(limbGroup);
    tree.appendChild(leafGroup);
    this.svg.appendChild(tree);

    if (animate) {
      // Grow up from the ground: wipe the clip from fully-clipped (bottom)
      // to fully-revealed (top). The fade keeps the leading edge soft.
      this.svg.animate(
        [{ clipPath: "inset(100% 0 0 0)" }, { clipPath: "inset(0% 0 0 0)" }],
        { duration: GROW_IN_MS, easing: GROW_EASING, fill: "backwards" },
      );
      tree.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: FADE_IN_MS, easing: GROW_EASING, fill: "backwards" },
      );
    }
  }

  private parsedFoliage: Element | null = null;

  /** Parse the leaf markup once (as SVG), then clone per instance. */
  private foliageTemplate(): Element {
    if (!this.parsedFoliage) {
      const doc = new DOMParser().parseFromString(
        `<svg xmlns="${SVG_NS}">${FOLIAGE_MARKUP}</svg>`,
        "image/svg+xml",
      );
      this.parsedFoliage = doc.documentElement.firstElementChild!;
    }
    return this.parsedFoliage;
  }

  private makePath(d: string, className: string): SVGPathElement {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("class", className);
    return path;
  }
}
